//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import {
    PROFILE_SOCIAL_MEDIA,
    PROFILE_SOCIAL_MEDIA_FACEBOOK,
    PROFILE_SOCIAL_MEDIA_LINKEDIN,
    PROFILE_SOCIAL_MEDIA_YOUTUBE,
    PROFILE_SOCIAL_MEDIA_INSTAGRAM,
    PROFILE_SOCIAL_MEDIA_WEBSITE,
    PROFILE_SOCIAL_MEDIA_TIKTOK,
    PROFILE_SOCIAL_MEDIA_GITHUB,
    PROFILE_SOCIAL_MEDIA_X,
} from '../../common/constants';
import {
    PLACEHOLDER_FIELD_FACEBOOK_URL,
    PLACEHOLDER_FIELD_GITHUB_URL,
    PLACEHOLDER_FIELD_INSTAGRAM_URL,
    PLACEHOLDER_FIELD_LINKEDIN_URL,
    PLACEHOLDER_FIELD_TIKTOK_URL,
    PLACEHOLDER_FIELD_TWITTER_URL,
    PLACEHOLDER_FIELD_WEBSITE_URL,
    PLACEHOLDER_FIELD_YOUTUBE_URL,
} from '../../common/placeholderHelper';
import { getHttpsUrl, isValidUrl } from '../../common/linkHelper';
import { saveProfileSocialMedia as saveProfileEmpSocialMedia } from '../../common/services/empMyProfileServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { saveProfileSocialMedia } from '../../common/services/myProfileServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _forEach from 'lodash/forEach';
import _isEmpty from 'lodash/isEmpty';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { data = {} } = params;
    const { facebookUrl, linkedInUrl, youTubeUrl, instagramUrl, twitterUrl, websiteUrl, tikTokUrl, gitHubUrl } = data;

    return makeStyles(theme => {
        const socialMediaBlockStyles = {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            borderRadius: '4px',
            margin: '13px 0 0 0',
            border: '2px solid #D5D7DD',

            '&:focus-within': {
                background: '#FCFFFF',
                borderColor: `${theme.palette.secondary.main}`,
                border: '2px solid',
            },
        };

        const socialMediaBlockNotConnectedStyles = {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            borderRadius: '4px',
            margin: '13px 0 0 0',
            border: '2px dashed #D5D7DD',

            '&:focus-within': {
                background: '#FCFFFF',
                borderColor: `${theme.palette.secondary.main}`,
                border: '2px solid',
            },
        };

        const facebookBlockStyles = _isEmpty(facebookUrl) ? socialMediaBlockNotConnectedStyles : socialMediaBlockStyles;
        const linkedInBlockStyles = _isEmpty(linkedInUrl) ? socialMediaBlockNotConnectedStyles : socialMediaBlockStyles;
        const youTubeBlockStyles = _isEmpty(youTubeUrl) ? socialMediaBlockNotConnectedStyles : socialMediaBlockStyles;
        const instagramBlockStyles = _isEmpty(instagramUrl) ? socialMediaBlockNotConnectedStyles : socialMediaBlockStyles;
        const twitterBlockStyles = _isEmpty(twitterUrl) ? socialMediaBlockNotConnectedStyles : socialMediaBlockStyles;
        const websiteBlockStyles = _isEmpty(websiteUrl) ? socialMediaBlockNotConnectedStyles : socialMediaBlockStyles;
        const tiktokBlockStyles = _isEmpty(tikTokUrl) ? socialMediaBlockNotConnectedStyles : socialMediaBlockStyles;
        const gitHubBlockStyles = _isEmpty(gitHubUrl) ? socialMediaBlockNotConnectedStyles : socialMediaBlockStyles;

        return {
            root: {},
            dialogContentContainer: {
                minHeight: '200px',
            },
            fieldContainer: {
                marginBottom: '24px',
            },
            lastFieldContainer: {
                marginBottom: '62px',
            },

            socialMediaBlockTitle: {
                fontFamily: 'Roboto, sans-serif',
                fontSize: '16px',
                lineHeight: '19px',
                color: '#111E14',
                fontWeight: '600',
                padding: '15px 21px 10px 15px',
            },
            socialMediaBlockLabel: {
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                lineHeight: '16px',
                color: '#111E14',
                fontWeight: '600',
                padding: '10px 21px 10px 15px',
            },
            socialMediaBlockUrl: {
                fontFamily: 'Roboto, sans-serif',
                fontSize: '12px',
                lineHeight: '14px',
                color: '#9B9E9C',
                fontWeight: '500',
                padding: '0px 21px 10px 15px',
            },

            urlInputContainer: {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0px 21px 15px 15px',
            },
            urlInputRoot: {
                paddingRight: '10px',
                backgroundColor: '#fff',

                '&$urlInputFocused $urlInputNotchedOutline': {
                    color: 'black',
                    borderColor: `${theme.palette.secondary.main} !important`,
                },
            },
            urlInput: {
                fontSize: '14px',
                borderColor: `${theme.palette.secondary.main} !important`,
            },
            urlInputFocused: {},
            urlInputNotchedOutline: {},
            urlHelperText: {
                textAlign: 'right',
            },

            facebookBlock: { ...facebookBlockStyles },
            linkedInBlock: { ...linkedInBlockStyles },
            youTubeBlock: { ...youTubeBlockStyles },
            instagramBlock: { ...instagramBlockStyles },
            twitterBlock: { ...twitterBlockStyles },
            websiteBlock: { ...websiteBlockStyles },
            tiktokBlock: { ...tiktokBlockStyles },
            gitHubBlock: { ...gitHubBlockStyles },
        };
    });
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // userId,
        // employerId,
        props,
        isDirty,
        setIsDirty,
        cleanUpDialog,
        data,
        setData,
        errors,
        // setErrors,
        // requestSave,
        setRequestSave,
        invalidUrlErrorDialogOpen,
        setInvalidUrlErrorDialogOpen,
        // setSnackbarMessage,
        // setSnackbarErrorMessage,
        saveLoading,
        // setSaveLoading,
    } = params;
    const { open } = props;
    const {
        // socialMediaBlock: socialMediaBlockCls,
        // socialMediaBlockNotConnected: socialMediaBlockNotConnectedCls,
        urlInput: urlInputCls,
        urlInputRoot: urlInputRootCls,
        urlInputFocused: urlInputFocusedCls,
        urlInputNotchedOutline: urlInputNotchedOutlineCls,
        // urlHelperText: urlHelperTextCls,
        // ...otherClasses
    } = classes;
    const defns = {
        root: {
            label: PROFILE_SOCIAL_MEDIA,
            open,
            isDirty,
            onClose: handleCloseDialog.bind(null, cleanUpDialog),
            actionButtonDefns: {
                save: {
                    disabled: !isDirty,
                    loading: saveLoading,
                    onClick: handleClickSaveButton.bind(null, data, setData, setRequestSave),
                },
            },
        },

        facebookBlock: {},
        linkedInBlock: {},
        youTubeBlock: {},
        instagramBlock: {},
        twitterBlock: {},
        websiteBlock: {},
        tiktokBlock: {},
        gitHubBlock: {},

        facebookUrlInput: {
            fullWidth: true,
            variant: 'outlined',
            size: 'small',
            placeholder: PLACEHOLDER_FIELD_FACEBOOK_URL,
            InputProps: {
                classes: {
                    root: urlInputRootCls,
                    focused: urlInputFocusedCls,
                    notchedOutline: urlInputNotchedOutlineCls,
                    input: urlInputCls,
                },
            },
            inputProps: {
                maxLength: 400,
            },
            // FormHelperTextProps: {
            //     className: urlHelperTextCls,
            // },
            value: data?.facebookUrl,
            onChange: handleChangeUrl.bind(null, 'facebookUrl', setData, setIsDirty),
            error: errors?.facebookUrl,
        },
        linkedInUrlInput: {
            fullWidth: true,
            variant: 'outlined',
            size: 'small',
            placeholder: PLACEHOLDER_FIELD_LINKEDIN_URL,
            InputProps: {
                classes: {
                    root: urlInputRootCls,
                    focused: urlInputFocusedCls,
                    notchedOutline: urlInputNotchedOutlineCls,
                    input: urlInputCls,
                },
            },
            inputProps: {
                maxLength: 400,
            },
            // FormHelperTextProps: {
            //     className: urlHelperTextCls,
            // },
            value: data?.linkedInUrl,
            onChange: handleChangeUrl.bind(null, 'linkedInUrl', setData, setIsDirty),
            error: errors?.linkedInUrl,
        },
        youTubeUrlInput: {
            fullWidth: true,
            variant: 'outlined',
            size: 'small',
            placeholder: PLACEHOLDER_FIELD_YOUTUBE_URL,
            InputProps: {
                classes: {
                    root: urlInputRootCls,
                    focused: urlInputFocusedCls,
                    notchedOutline: urlInputNotchedOutlineCls,
                    input: urlInputCls,
                },
            },
            inputProps: {
                maxLength: 400,
            },
            // FormHelperTextProps: {
            //     className: urlHelperTextCls,
            // },
            value: data?.youTubeUrl,
            onChange: handleChangeUrl.bind(null, 'youTubeUrl', setData, setIsDirty),
            error: errors?.youTubeUrl,
        },
        instagramUrlInput: {
            fullWidth: true,
            variant: 'outlined',
            size: 'small',
            placeholder: PLACEHOLDER_FIELD_INSTAGRAM_URL,
            InputProps: {
                classes: {
                    root: urlInputRootCls,
                    focused: urlInputFocusedCls,
                    notchedOutline: urlInputNotchedOutlineCls,
                    input: urlInputCls,
                },
            },
            inputProps: {
                maxLength: 400,
            },
            // FormHelperTextProps: {
            //     className: urlHelperTextCls,
            // },
            value: data?.instagramUrl,
            onChange: handleChangeUrl.bind(null, 'instagramUrl', setData, setIsDirty),
            error: errors?.instagramUrl,
        },
        twitterUrlInput: {
            fullWidth: true,
            variant: 'outlined',
            size: 'small',
            placeholder: PLACEHOLDER_FIELD_TWITTER_URL,
            InputProps: {
                classes: {
                    root: urlInputRootCls,
                    focused: urlInputFocusedCls,
                    notchedOutline: urlInputNotchedOutlineCls,
                    input: urlInputCls,
                },
            },
            inputProps: {
                maxLength: 400,
            },
            // FormHelperTextProps: {
            //     className: urlHelperTextCls,
            // },
            value: data?.twitterUrl,
            onChange: handleChangeUrl.bind(null, 'twitterUrl', setData, setIsDirty),
            error: errors?.twitterUrl,
        },
        websiteUrlInput: {
            fullWidth: true,
            variant: 'outlined',
            size: 'small',
            placeholder: PLACEHOLDER_FIELD_WEBSITE_URL,
            InputProps: {
                classes: {
                    root: urlInputRootCls,
                    focused: urlInputFocusedCls,
                    notchedOutline: urlInputNotchedOutlineCls,
                    input: urlInputCls,
                },
            },
            inputProps: {
                maxLength: 400,
            },
            // FormHelperTextProps: {
            //     className: urlHelperTextCls,
            // },
            value: data?.websiteUrl,
            onChange: handleChangeUrl.bind(null, 'websiteUrl', setData, setIsDirty),
            error: errors?.websiteUrl,
        },
        tikTokUrlInput: {
            fullWidth: true,
            variant: 'outlined',
            size: 'small',
            placeholder: PLACEHOLDER_FIELD_TIKTOK_URL,
            InputProps: {
                classes: {
                    root: urlInputRootCls,
                    focused: urlInputFocusedCls,
                    notchedOutline: urlInputNotchedOutlineCls,
                    input: urlInputCls,
                },
            },
            inputProps: {
                maxLength: 400,
            },
            value: data?.tikTokUrl,
            onChange: handleChangeUrl.bind(null, 'tikTokUrl', setData, setIsDirty),
            error: errors?.tikTokUrl,
        },
        gitHubUrlInput: {
            fullWidth: true,
            variant: 'outlined',
            size: 'small',
            placeholder: PLACEHOLDER_FIELD_GITHUB_URL,
            InputProps: {
                classes: {
                    root: urlInputRootCls,
                    focused: urlInputFocusedCls,
                    notchedOutline: urlInputNotchedOutlineCls,
                    input: urlInputCls,
                },
            },
            inputProps: {
                maxLength: 400,
            },
            value: data?.gitHubUrl,
            onChange: handleChangeUrl.bind(null, 'gitHubUrl', setData, setIsDirty),
            error: errors?.gitHubUrl,
        },
        invalidUrlErrorDialog: {
            message: "One or more of the entered URL's are invalid.",
            open: invalidUrlErrorDialogOpen,
            onClose: () => setInvalidUrlErrorDialogOpen(false),
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const initialValues = {
    facebookUrl: null,
    linkedInUrl: null,
    youTubeUrl: null,
    instagramUrl: null,
    twitterUrl: null,
    websiteUrl: null,
    tikTokUrl: null,
    gitHubUrl: null,
};

const fieldNameList = ['facebookUrl', 'linkedInUrl', 'youTubeUrl', 'instagramUrl', 'twitterUrl', 'websiteUrl', 'tikTokUrl', 'gitHubUrl'];

const handleCloseDialog = cleanUpDialog => {
    cleanUpDialog();
};

const handleChangeUrl = (name, setData, setIsDirty, evt) => {
    const value = evt.target.value;
    setData(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
};

const validate = data => {
    const errors = {};

    _forEach(fieldNameList, key => {
        const url = data[key];
        if (url) {
            if (!isValidUrl(url)) {
                _set(errors, key, 'Ohoh');
            }
        }
    });

    return errors;
};

const handleClickSaveButton = async (data, setData, setRequestSave) => {
    const dto = {};

    // prepend https:// if missing
    _forEach(fieldNameList, key => {
        const url = data[key];
        _set(dto, key, getHttpsUrl(url));
    });

    // update data state
    setData(prev => ({ ...prev, ...dto }));

    // request save
    setRequestSave(true);
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const SocialMediaPortfolioDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const dialogEl = useRef();
    const appContext = useContext(AppContext);
    const context = useContext(LayoutContext);
    const { userId, employerId } = appContext;
    const { setSnackbarMessage, setSnackbarErrorMessage } = context;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isDirty, setIsDirty] = useState(false);
    const [data, setData] = useState(initialValues);
    const [errors, setErrors] = useState({});
    const [requestSave, setRequestSave] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [invalidUrlErrorDialogOpen, setInvalidUrlErrorDialogOpen] = useState(false);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { onClose, open } = props;
    const profileData = _get(props, 'data', {});

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const cleanUpDialog = useCallback(() => {
        setIsDirty(false); // reset dirty flag
        onClose(); // fire onClose event
    }, [onClose]);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        if (open) {
            const facebookUrl = _get(profileData, 'facebookUrl');
            const linkedInUrl = _get(profileData, 'linkedInUrl');
            const youTubeUrl = _get(profileData, 'youTubeUrl');
            const instagramUrl = _get(profileData, 'instagramUrl');
            const twitterUrl = _get(profileData, 'twitterUrl');
            const websiteUrl = _get(profileData, 'websiteUrl');
            const tikTokUrl = _get(profileData, 'tikTokUrl');
            const gitHubUrl = _get(profileData, 'gitHubUrl');

            setData(prev => ({ ...prev, facebookUrl, linkedInUrl, youTubeUrl, instagramUrl, twitterUrl, websiteUrl, tikTokUrl, gitHubUrl }));
        }
    }, [profileData, open]);

    // request save
    useEffect(() => {
        const saveData = async () => {
            if (employerId) {
                setSaveLoading(true);
                await saveProfileEmpSocialMedia({ employerId, ...data, setSnackbarMessage, setSnackbarErrorMessage });
                setSaveLoading(false);
            } else {
                setSaveLoading(true);
                await saveProfileSocialMedia({ userId, ...data, setSnackbarMessage, setSnackbarErrorMessage });
                setSaveLoading(false);
            }

            cleanUpDialog();
        };

        if (requestSave) {
            setData(prev => ({ ...prev }));

            // validate
            const errors = validate(data);

            if (_isEmpty(errors)) {
                // validation passed
                saveData();
            } else {
                // validation failed
                setInvalidUrlErrorDialogOpen(true);
                setErrors(errors);
            }

            // reset flag
            setRequestSave(false);
        }
    }, [data, requestSave, userId, employerId, cleanUpDialog, setSnackbarMessage, setSnackbarErrorMessage]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const classes = useStyles({ data })();

    const defns = getDefns({
        classes,
        userId,
        employerId,
        props,
        isDirty,
        setIsDirty,
        cleanUpDialog,
        data,
        setData,
        errors,
        setErrors,
        requestSave,
        setRequestSave,
        invalidUrlErrorDialogOpen,
        setInvalidUrlErrorDialogOpen,
        setSnackbarMessage,
        setSnackbarErrorMessage,
        saveLoading,
        setSaveLoading,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <AfitdEditDialog ref={dialogEl} {...defns.root}>
            <div {...defns.dialogContentContainer}>
                <div {...defns.facebookBlock}>
                    <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_FACEBOOK}</AfitdTypography>
                    <AfitdTypography {...defns.socialMediaBlockLabel}>Enter your {PROFILE_SOCIAL_MEDIA_FACEBOOK} Profile URL</AfitdTypography>
                    <div {...defns.urlInputContainer}>
                        <AfitdTextField {...defns.facebookUrlInput} />
                    </div>
                </div>
                <div {...defns.linkedInBlock}>
                    <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_LINKEDIN}</AfitdTypography>
                    <AfitdTypography {...defns.socialMediaBlockLabel}>Enter your {PROFILE_SOCIAL_MEDIA_LINKEDIN} Profile URL</AfitdTypography>
                    <div {...defns.urlInputContainer}>
                        <AfitdTextField {...defns.linkedInUrlInput} />
                    </div>
                </div>
                <div {...defns.instagramBlock}>
                    <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_INSTAGRAM}</AfitdTypography>
                    <AfitdTypography {...defns.socialMediaBlockLabel}>Enter your {PROFILE_SOCIAL_MEDIA_INSTAGRAM} Profile URL</AfitdTypography>
                    <div {...defns.urlInputContainer}>
                        <AfitdTextField {...defns.instagramUrlInput} />
                    </div>
                </div>
                <div {...defns.twitterBlock}>
                    <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_X}</AfitdTypography>
                    <AfitdTypography {...defns.socialMediaBlockLabel}>Enter your {PROFILE_SOCIAL_MEDIA_X} Profile URL</AfitdTypography>
                    <div {...defns.urlInputContainer}>
                        <AfitdTextField {...defns.twitterUrlInput} />
                    </div>
                </div>
                <div {...defns.youTubeBlock}>
                    <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_YOUTUBE}</AfitdTypography>
                    <AfitdTypography {...defns.socialMediaBlockLabel}>Enter your {PROFILE_SOCIAL_MEDIA_YOUTUBE} Profile URL</AfitdTypography>
                    <div {...defns.urlInputContainer}>
                        <AfitdTextField {...defns.youTubeUrlInput} />
                    </div>
                </div>
                <div {...defns.tiktokBlock}>
                    <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_TIKTOK}</AfitdTypography>
                    <AfitdTypography {...defns.socialMediaBlockLabel}>Enter your {PROFILE_SOCIAL_MEDIA_TIKTOK} URL</AfitdTypography>
                    <div {...defns.urlInputContainer}>
                        <AfitdTextField {...defns.tikTokUrlInput} />
                    </div>
                </div>
                <div {...defns.gitHubBlock}>
                    <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_GITHUB}</AfitdTypography>
                    <AfitdTypography {...defns.socialMediaBlockLabel}>Enter your {PROFILE_SOCIAL_MEDIA_GITHUB} URL</AfitdTypography>
                    <div {...defns.urlInputContainer}>
                        <AfitdTextField {...defns.gitHubUrlInput} />
                    </div>
                </div>
                <div {...defns.websiteBlock}>
                    <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_WEBSITE}</AfitdTypography>
                    <AfitdTypography {...defns.socialMediaBlockLabel}>Enter your {PROFILE_SOCIAL_MEDIA_WEBSITE} URL</AfitdTypography>
                    <div {...defns.urlInputContainer}>
                        <AfitdTextField {...defns.websiteUrlInput} />
                    </div>
                </div>
            </div>
            <AfitdMessageDialog {...defns.invalidUrlErrorDialog} />
        </AfitdEditDialog>
    );
};

export default SocialMediaPortfolioDialog;
